import type { ComboboxOption } from "@replo/design-system/components/combobox/ComboboxContext";
import type { LogMessage } from "./useNextDevServerHook";

import * as React from "react";
import { useRef } from "react";

import { useLocalStorageState } from "@editor/hooks/useLocalStorage";

import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import twMerge from "@replo/design-system/utils/twMerge";
import { PanelBottom, PanelLeft, PanelRight, PanelTop, X } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

import { useNextDevServer } from "./context";

const formatLogMessage = (log: LogMessage) => {
  return `${log.date.toLocaleTimeString()} ${log.message}`;
};

type Position = "left" | "right" | "bottom" | "top";

type WebContainerLogsProps = {
  onClose?: () => void;
};

const getPositionClasses = (position: Position) => {
  return exhaustiveSwitch({ type: position })({
    bottom: "fixed bottom-0 left-0 right-0",
    top: "fixed top-0 left-0 right-0",
    left: "fixed top-0 bottom-0 left-0",
    right: "fixed top-0 bottom-0 right-0",
  });
};

const getPositionIcon = (position: Position) => {
  return exhaustiveSwitch({ type: position })({
    bottom: <PanelBottom size={16} />,
    top: <PanelTop size={16} />,
    left: <PanelLeft size={16} />,
    right: <PanelRight size={16} />,
  });
};

const WebContainerLogs = ({ onClose }: WebContainerLogsProps) => {
  const { logs } = useNextDevServer();
  const [position, setPosition] = useLocalStorageState(
    "replo:debug:code-execution-logs-position",
    "bottom" as Position,
    { schema: z.enum(["left", "right", "bottom", "top"]) },
  );

  const logsRef = useRef<HTMLPreElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to scroll to the bottom of the logs when they change
  React.useEffect(() => {
    if (logsRef.current) {
      logsRef.current?.scrollTo({
        top: logsRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  const positionOptions = [
    {
      value: "bottom",
      label: (
        <MenuItem variant="default" startEnhancer={<PanelBottom size={16} />}>
          Bottom
        </MenuItem>
      ),
      displayValue: "Bottom",
    },
    {
      value: "top",
      label: (
        <MenuItem variant="default" startEnhancer={<PanelTop size={16} />}>
          Top
        </MenuItem>
      ),
      displayValue: "Top",
    },
    {
      value: "left",
      label: (
        <MenuItem variant="default" startEnhancer={<PanelLeft size={16} />}>
          Left
        </MenuItem>
      ),
      displayValue: "Left",
    },
    {
      value: "right",
      label: (
        <MenuItem variant="default" startEnhancer={<PanelRight size={16} />}>
          Right
        </MenuItem>
      ),
      displayValue: "Right",
    },
  ] satisfies ComboboxOption[];

  return (
    <div
      className={twMerge(
        getPositionClasses(position),
        "z-50 bg-white flex flex-col border-1",
      )}
    >
      <div className="absolute right-5.5 top-2 flex items-center gap-2">
        <Combobox.Root
          options={positionOptions}
          value={position}
          onChange={(value) => setPosition(value as Position)}
        >
          <Combobox.Trigger>
            <IconButton
              variant="tertiary"
              size="sm"
              icon={getPositionIcon(position)}
              tooltipText="Change position"
              UNSAFE_className="bg-white"
            />
          </Combobox.Trigger>
          <Combobox.Popover layoutClassName="w-[125px]">
            <Combobox.Content />
          </Combobox.Popover>
        </Combobox.Root>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard
              .writeText(
                logs.map(formatLogMessage).join("\n").replaceAll(/\n+/g, "\n"),
              )
              .then(() => {
                toast({
                  header: "Logs copied to clipboard",
                });
              });
          }}
        >
          Copy Logs
        </Button>
        {onClose && (
          <IconButton
            variant="tertiary"
            size="sm"
            onClick={onClose}
            icon={<X size={16} />}
            tooltipText="Close Logs"
            UNSAFE_className="bg-white"
          />
        )}
      </div>
      <pre
        ref={logsRef}
        className={twMerge(
          // eslint-disable-next-line replo/no-tailwind-literal-color
          "overflow-x-auto overflow-y-auto bg-black p-2 text-xs text-green-400",
          position === "left" && "w-96 h-full",
          position === "right" && "w-96 h-full",
          position === "top" && "h-96",
          position === "bottom" && "h-96",
        )}
      >
        {logs.map((log, index) => (
          <div key={index}>{formatLogMessage(log)}</div>
        ))}
      </pre>
    </div>
  );
};

export default WebContainerLogs;
